// Duplicates of commercial contacts and companies (pure; the server applies it: team.mts › checkDuplicate and
// airtable-proxy for the team of the central base). Tests: npm run test:commercial-team.
//   block — a reliable match: the same email, the same LinkedIn profile (contact) or the same website (company).
//   warn  — only a hint: the same or a similar name. A similar name, or sharing an email domain, does not prove that
//           two records are the same, so it never blocks and nothing is merged.
// What a match reveals is decided by the caller (never another person's data outside their reach).

const fold = (v) => String(v ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
export const normEmail = (v) => {
  const e = String(v ?? '').trim().toLowerCase().replace(/^mailto:/, '');
  return /^[^\s@<>"',;:()]+@[^\s@<>"',;:()]+\.[a-z]{2,}$/.test(e) ? e : '';
};
/** A LinkedIn profile URL → "linkedin.com/in/<slug>" (or /company/<slug>), else ''. */
export function normLinkedIn(v) {
  const m = String(v ?? '').trim().toLowerCase().match(/linkedin\.com\/(in|company|pub)\/([^/?#\s]+)/);
  return m ? `linkedin.com/${m[1]}/${decodeURIComponent(m[2]).replace(/\/+$/, '')}` : '';
}
// Hosts that say nothing about which company it is (a profile or a free site builder page)
const GENERIC_HOSTS = ['linkedin.com', 'facebook.com', 'instagram.com', 'x.com', 'twitter.com', 'google.com', 'sites.google.com', 'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'wix.com', 'wixsite.com', 'wordpress.com', 'blogspot.com', 'linktr.ee', 'notion.site', 'youtube.com', 'tiktok.com', 'github.com', 'bit.ly'];
/** A website → its host without "www." (lower case), '' when empty or generic. */
export function normWebsite(v) {
  let s = String(v ?? '').trim().toLowerCase();
  if (!s) return '';
  s = s.replace(/^[a-z]+:\/\//, '').replace(/^www\./, '').split(/[/?#\s]/)[0].replace(/:\d+$/, '').replace(/\.$/, '');
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(s)) return '';
  return GENERIC_HOSTS.some(g => s === g || s.endsWith(`.${g}`)) ? '' : s;
}
const LEGAL = /\b(s\.?a\.?s?|s\.?r\.?l|s\.?l\.?u?|s\.?a\.?c|s\.?p\.?a|ltda?|inc|llc|ltd|corp|co|gmbh|sociedad anonima|limitada|group|grupo)\b/g;
/** A company name without accents, punctuation or legal suffixes. */
export const normCompany = (v) => fold(v).replace(/[.]/g, '').replace(/,/g, ' ').replace(LEGAL, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
export const normPerson = (v) => fold(v).replace(/[^a-z0-9]+/g, ' ').trim();

/** Similarity of two short texts, 0..1 (bigram Dice). */
export function similarity(a, b) {
  const x = String(a || ''), y = String(b || '');
  if (!x || !y) return 0;
  if (x === y) return 1;
  const grams = (s) => { const g = new Map(); for (let i = 0; i < s.length - 1; i++) { const k = s.slice(i, i + 2); g.set(k, (g.get(k) || 0) + 1); } return g; };
  const ga = grams(x), gb = grams(y);
  let both = 0;
  for (const [k, n] of ga) both += Math.min(n, gb.get(k) || 0);
  const total = Math.max(1, x.length - 1 + y.length - 1);
  return (2 * both) / total;
}
export const SIMILAR_NAME = 0.85;

const fullName = (f) => normPerson(`${f?.['Name'] || ''} ${f?.['Last name'] || ''}`);

/**
 * The records that may be the same as `fields` (a new or edited contact or company), each once with its strongest
 * reason. kind: 'contact' | 'account'; records: the existing ones (Airtable shape); excludeId: the record edited.
 */
export function duplicateMatches(kind, fields = {}, records = [], { excludeId = '' } = {}) {
  const out = new Map();
  const add = (r, level, reason) => {
    const prev = out.get(r.id);
    if (!prev || (prev.level === 'warn' && level === 'block')) out.set(r.id, { id: r.id, level, reason });
  };
  if (kind === 'contact') {
    const email = normEmail(fields['Email']);
    const li = normLinkedIn(fields['LinkedIn']);
    const name = fullName(fields);
    for (const r of records) {
      if (!r?.id || r.id === excludeId) continue;
      const f = r.fields || {};
      if (email && normEmail(f['Email']) === email) add(r, 'block', 'same_email');
      if (li && normLinkedIn(f['LinkedIn']) === li) add(r, 'block', 'same_linkedin');
      const other = fullName(f);
      if (name && other && name.includes(' ') && (other === name || similarity(other, name) >= SIMILAR_NAME)) add(r, 'warn', other === name ? 'same_name' : 'similar_name');
    }
  } else {
    const site = normWebsite(fields['Website']);
    const name = normCompany(fields['Account Name']);
    for (const r of records) {
      if (!r?.id || r.id === excludeId) continue;
      const f = r.fields || {};
      if (site && normWebsite(f['Website']) === site) add(r, 'block', 'same_website');
      const other = normCompany(f['Account Name']);
      if (name && other && (other === name || similarity(other, name) >= SIMILAR_NAME)) add(r, 'warn', other === name ? 'same_name' : 'similar_name');
    }
  }
  return [...out.values()].sort((a, b) => (a.level === b.level ? 0 : a.level === 'block' ? -1 : 1));
}

/** Whether a write touches the fields that decide a reliable duplicate (so it has to be checked). */
export const touchesDuplicateFields = (kind, fields = {}) => (kind === 'contact' ? ['Email', 'LinkedIn'] : ['Website']).some(k => Object.prototype.hasOwnProperty.call(fields, k));

export const DUPLICATE_REASONS = {
  same_email: 'el mismo email', same_linkedin: 'el mismo perfil de LinkedIn', same_website: 'el mismo sitio web',
  same_name: 'el mismo nombre', similar_name: 'un nombre parecido',
};
